export interface Vec3 { x: number; y: number; z: number }

export const v3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });

export const dist = (a: Vec3, b: Vec3): number =>
  Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);

/** Horizontal distance. Capture zones use this so flying cannot dodge them. */
export const dist2d = (a: Vec3, b: Vec3): number => Math.hypot(a.x - b.x, a.z - b.z);

export const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

/**
 * Falloff with a flat plateau at the centre.
 *
 * Full strength anywhere within `plateau`, then a logarithmic tail out to
 * `range`. The plateau matters: a pool is a place you stand in, not a point you
 * balance on, and without it the edge of a four-metre pool paid barely half what
 * the exact centre did.
 */
export function falloff(distance: number, range: number, plateau = 0): number {
  if (distance <= plateau) return 1;
  if (distance >= range) return 0;
  const d = distance - plateau;
  const r = range - plateau;
  return clamp(1 - Math.log(d + 1) / Math.log(r + 1), 0, 1);
}

/**
 * Direction conventions, defined once so nothing can disagree with anything else.
 *
 * Three.js cameras look down −Z with +Y up, so at yaw 0 forward is (0, 0, −1)
 * and right is (+1, 0, 0). Increasing yaw turns left. Both the simulation (for
 * aiming spells) and the controller (for walking) derive from these two
 * functions; when they were written separately, walking came out negated.
 */
export function forwardVector(yaw: number, pitch: number): Vec3 {
  const cp = Math.cos(pitch);
  return { x: -Math.sin(yaw) * cp, y: Math.sin(pitch), z: -Math.cos(yaw) * cp };
}

/** Horizontal right-hand vector for the given yaw. */
/**
 * Up, from the firer's point of view rather than the world's.
 *
 * The cross of forward and right, so it tilts with pitch — which is what a
 * volley's triangle needs. Using world up instead would leave the formation
 * square to the horizon while the caster looked at their feet, and three axes
 * that arrange themselves relative to the ground rather than to the aim are
 * three axes that miss together.
 */
export function upVector(yaw: number, pitch: number): Vec3 {
  const f = forwardVector(yaw, pitch);
  const r = rightVector(yaw);
  return {
    x: r.y * f.z - r.z * f.y,
    y: r.z * f.x - r.x * f.z,
    z: r.x * f.y - r.y * f.x,
  };
}

export function rightVector(yaw: number): Vec3 {
  return { x: Math.cos(yaw), y: 0, z: -Math.sin(yaw) };
}

/**
 * Horizontal movement direction from WASD input, normalised so diagonals are
 * not faster. `forward` is +1 for W, `right` is +1 for D.
 */
export function moveVector(yaw: number, forward: number, right: number): { x: number; z: number } {
  const f = forwardVector(yaw, 0);
  const r = rightVector(yaw);
  let x = f.x * forward + r.x * right;
  let z = f.z * forward + r.z * right;
  const len = Math.hypot(x, z);
  if (len > 1) {
    x /= len;
    z /= len;
  }
  return { x, z };
}

/**
 * Segment against an axis-aligned box centred on the origin, by the slab method.
 *
 * Barriers use this after rotating the segment into their own local space, which
 * is cheaper and far easier to reason about than an oriented-box test. It is how
 * core decides whether a spell got past a wall without asking Rapier — the server
 * has to reach the same verdict and it has no physics world to ask.
 */
export function segmentIntersectsAabb(from: Vec3, to: Vec3, half: Vec3): boolean {
  let tmin = 0;
  let tmax = 1;
  const axes = ['x', 'y', 'z'] as const;
  for (const a of axes) {
    const origin = from[a];
    const delta = to[a] - from[a];
    const h = half[a];
    if (Math.abs(delta) < 1e-9) {
      // Parallel to this slab: either inside it for the whole segment, or never.
      if (origin < -h || origin > h) return false;
      continue;
    }
    let t1 = (-h - origin) / delta;
    let t2 = (h - origin) / delta;
    if (t1 > t2) [t1, t2] = [t2, t1];
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return false;
  }
  return true;
}

/**
 * Closest approach between a segment and a vertical capsule, as the point on
 * the *segment* where it happens.
 *
 * A projectile is tested along its whole step rather than at its endpoint. At
 * 60Hz an Ice Dart covers 0.8m a tick, which is more than a body is wide, so an
 * endpoint test lets fast spells pass clean through anyone they should have hit.
 * Returning the point rather than a boolean is what makes it possible to ask
 * *where* on the body it landed.
 */
export function segmentHitsCapsule(
  from: Vec3, to: Vec3, base: Vec3, height: number, radius: number,
): Vec3 | null {
  // The capsule's core segment: its hemispherical caps are what the radius test
  // below accounts for, so the axis stops one radius short at each end.
  const axisFrom = { x: base.x, y: base.y + radius, z: base.z };
  const axisTo = { x: base.x, y: base.y + Math.max(height - radius, radius), z: base.z };

  const d1 = { x: to.x - from.x, y: to.y - from.y, z: to.z - from.z };
  const d2 = { x: axisTo.x - axisFrom.x, y: axisTo.y - axisFrom.y, z: axisTo.z - axisFrom.z };
  const r = { x: from.x - axisFrom.x, y: from.y - axisFrom.y, z: from.z - axisFrom.z };

  const a = d1.x * d1.x + d1.y * d1.y + d1.z * d1.z;
  const e = d2.x * d2.x + d2.y * d2.y + d2.z * d2.z;
  const f = d2.x * r.x + d2.y * r.y + d2.z * r.z;

  let s = 0;
  let t = 0;
  if (a < 1e-9) {
    t = e < 1e-9 ? 0 : Math.min(1, Math.max(0, f / e));
  } else {
    const c = d1.x * r.x + d1.y * r.y + d1.z * r.z;
    if (e < 1e-9) {
      s = Math.min(1, Math.max(0, -c / a));
    } else {
      const b = d1.x * d2.x + d1.y * d2.y + d1.z * d2.z;
      const denom = a * e - b * b;
      s = denom > 1e-9 ? Math.min(1, Math.max(0, (b * f - c * e) / denom)) : 0;
      t = (b * s + f) / e;
      if (t < 0) {
        t = 0;
        s = Math.min(1, Math.max(0, -c / a));
      } else if (t > 1) {
        t = 1;
        s = Math.min(1, Math.max(0, (b - c) / a));
      }
    }
  }

  const p1 = { x: from.x + d1.x * s, y: from.y + d1.y * s, z: from.z + d1.z * s };
  const p2 = { x: axisFrom.x + d2.x * t, y: axisFrom.y + d2.y * t, z: axisFrom.z + d2.z * t };
  return dist(p1, p2) <= radius ? p1 : null;
}

/**
 * Nudges a unit direction to a random point inside a cone around it.
 *
 * The randomness comes from the caller's generator rather than `Math.random`,
 * because the server has to arrive at the same shot: an unseeded spread would
 * mean every client and the server disagreed about where a spell went.
 */
export function spreadDirection(
  dir: Vec3, coneRadians: number, rand: () => number,
): Vec3 {
  if (coneRadians <= 0) return dir;

  // Uniform over the cap rather than over the angle, or shots would cluster in
  // the middle and the reticle would overstate how wide the spread really is.
  const cosTheta = 1 - rand() * (1 - Math.cos(coneRadians));
  const sinTheta = Math.sqrt(Math.max(0, 1 - cosTheta * cosTheta));
  const phi = rand() * Math.PI * 2;

  // Any two axes perpendicular to the aim will do; this picks one that stays
  // well-conditioned when the player is looking straight up or down.
  const up = Math.abs(dir.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const right = normalise(cross(up, dir));
  const local = cross(dir, right);

  return normalise({
    x: dir.x * cosTheta + (right.x * Math.cos(phi) + local.x * Math.sin(phi)) * sinTheta,
    y: dir.y * cosTheta + (right.y * Math.cos(phi) + local.y * Math.sin(phi)) * sinTheta,
    z: dir.z * cosTheta + (right.z * Math.cos(phi) + local.z * Math.sin(phi)) * sinTheta,
  });
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

function normalise(v: Vec3): Vec3 {
  const len = Math.hypot(v.x, v.y, v.z);
  return len < 1e-9 ? { x: 0, y: 0, z: 1 } : { x: v.x / len, y: v.y / len, z: v.z / len };
}
